import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = window.__DASHBAL_CONFIG__?.apiBaseUrl || 'http://localhost:3001';

// Generate or retrieve IDs
const getVisitorId = () => {
    let id = localStorage.getItem('dashbal_visitor_id');
    if (!id) {
        id = `v-${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('dashbal_visitor_id', id);
    }
    return id;
};

const getSessionId = () => {
    let id = sessionStorage.getItem('dashbal_session_id');
    if (!id) {
        id = `s-${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('dashbal_session_id', id);
    }
    return id;
};

const getUserId = () => {
    const authData = localStorage.getItem('auth-storage');
    if (authData) {
        try {
            const parsed = JSON.parse(authData);
            return parsed.state?.user?.id;
        } catch (e) { }
    }
    return null;
};

export const trackEvent = async (event: string, properties: Record<string, any> = {}) => {
    try {
        const context = {
            url: window.location.href,
            path: window.location.pathname,
            referrer: document.referrer,
            screen: { width: window.screen.width, height: window.screen.height },
            language: navigator.language,
        };

        await axios.post(`${API_URL}/track`, {
            event,
            properties: {
                ...properties,
                userId: getUserId(),
                sessionId: getSessionId(),
                anonymousId: getVisitorId(),
            },
            context,
        });
    } catch (error) {
        console.warn('[Tracking] Failed to track event', event);
    }
};

/**
 * Hook for automatic page view tracking
 */
export const usePageTracking = () => {
    const location = useLocation();

    useEffect(() => {
        trackEvent('page.view', {
            path: location.pathname,
            search: location.search,
            title: document.title,
        });
    }, [location]);
};

export const useVisitorPresence = () => {
    const wsRef = useRef<WebSocket | null>(null);
    const userIdRef = useRef<string | null>(getUserId());

    useEffect(() => {
        let retryCount = 0;
        const maxDelay = 30000;
        let isUnmounted = false;

        const connect = () => {
            if (isUnmounted || wsRef.current) return;

            const sessionId = getSessionId();
            const visitorId = getVisitorId();
            const currentUserId = userIdRef.current;

            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const host = API_URL.replace(/^http(s)?:\/\//, '');
            const wsUrl = `${protocol}//${host}/ws/tracking?sessionId=${sessionId}&anonymousId=${visitorId}${currentUserId ? `&userId=${currentUserId}` : ''}`;

            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                retryCount = 0;
            };

            ws.onclose = () => {
                wsRef.current = null;
                if (!isUnmounted) {
                    const delay = Math.min(Math.pow(2, retryCount) * 1000 + Math.random() * 1000, maxDelay);
                    retryCount++;
                    setTimeout(connect, delay);
                }
            };

            ws.onerror = () => {
                ws.close();
            };
        };

        connect();

        // Check for user ID changes every 2 seconds to force reconnect
        const checkUserIdInterval = setInterval(() => {
            const currentUserId = getUserId();
            if (currentUserId !== userIdRef.current) {
                userIdRef.current = currentUserId;
                if (wsRef.current) {
                    wsRef.current.close(); // Triggers onclose which reconnects with new ID
                }
            }
        }, 2000);

        return () => {
            isUnmounted = true;
            clearInterval(checkUserIdInterval);
            if (wsRef.current) {
                const ws = wsRef.current;
                ws.onclose = null;
                ws.close();
                wsRef.current = null;
            }
        };
    }, []);
};
